import type { FC } from "react";
import React, { useMemo, useRef, useCallback } from "react";

import cn from "classnames";

import Tabs from "./components/Tabs/Tabs";
import {
    TABS_VIEW_BUTTON_SCROLL_ID,
    TABS_VIEW_SKELETON_TEST_ID,
    TABS_VIEW_TEST_ID,
} from "./vars";
import ScrollContainer from "../../../helpers/wrappers/ScrollContainer/ScrollContainer";
import { generateFilledByIndexArray } from "../../../utils/natives/objects";

import type { TabViewsProps } from "./types";
import type {
    ScrollContainerProps,
    ScrollContainerRef,
} from "../../../helpers/wrappers/ScrollContainer/ScrollContainer";

import classes from "./TabsView.styl";

const TABS_VIEW_DISPLAY_NAME = "TabsView";

export const TabsView: FC<TabViewsProps> = ({
    customColorsStyles,
    children,
    view = "primary",
    offset = 200,
    skeletonsCount = 10,
    loading = false,
    labelLeft,
    labelRight,
    selectedTabIdx,
    onChangeSelectedTabIdx,
    customVarsStyles,
    divider,
}) => {
    const tabsRef = useRef<HTMLButtonElement[] | null>(null);
    const focusedTabIdxRef = useRef<number | null>(null);
    const buttonScrollRef = useRef<ScrollContainerRef>(null);

    const skeletonIds = useMemo(
        () => (loading ? generateFilledByIndexArray(skeletonsCount) : []),
        [loading, skeletonsCount],
    );

    const wrapperClasses = cn(classes["wrapper"], customColorsStyles, {
        [classes["__with-divider"]]: divider && view === "base",
    });

    const handleTabChange = useCallback(
        (tabIdx: number) => {
            onChangeSelectedTabIdx?.(tabIdx);
            const selectedTab = tabsRef.current?.[tabIdx];
            if (selectedTab && buttonScrollRef.current) {
                const tabLeft = selectedTab.offsetLeft;
                const tabWidth = selectedTab.offsetWidth;
                const scrollToPosition = tabLeft - offset / 2 + tabWidth / 2;
                buttonScrollRef.current.scrollTo(scrollToPosition, true);
            }
        },
        [onChangeSelectedTabIdx, tabsRef, buttonScrollRef, offset],
    );

    const content = useMemo(() => {
        if (loading) {
            return skeletonIds.map((id) => (
                <div
                    key={id}
                    className={classes["skeleton-tab"]}
                    data-tsid={TABS_VIEW_SKELETON_TEST_ID}
                />
            ));
        }
        return (
            <Tabs
                tabsRef={tabsRef}
                selectedTabIdx={selectedTabIdx}
                onTabFocus={(tabIdx) => {
                    focusedTabIdxRef.current = tabIdx;
                }}
                onChangeSelectedTabIdx={handleTabChange}
            >
                {children}
            </Tabs>
        );
    }, [children, loading, selectedTabIdx, skeletonIds, handleTabChange]);

    const scrollContainerProps = {
        "data-tsid": TABS_VIEW_BUTTON_SCROLL_ID,
        buttonRef: buttonScrollRef,
        labelLeft,
        labelRight,
        offset,
        view,
        customColorsStyles,
    } as ScrollContainerProps;

    return (
        <div
            className={cn(wrapperClasses, customVarsStyles)}
            data-tsid={TABS_VIEW_TEST_ID}
            data-uikit={TABS_VIEW_DISPLAY_NAME}
        >
            <ScrollContainer {...scrollContainerProps}>
                {content}
            </ScrollContainer>
        </div>
    );
};

TabsView.displayName = TABS_VIEW_DISPLAY_NAME;

export default TabsView;
